/**
 * Resource-area highlights for Tour Planner.
 *
 * City/state community resources are stored as a single resource_submissions
 * doc with scope 'city' | 'state' (not copied onto every venue). This module
 * loads Census place polygons for those cities (on demand) and styles fills
 * like the affiliate choropleth: same fill/outline treatment, with hue taken
 * from the venue-cluster color steps (more venues in the area → darker).
 */

import * as turf from '@turf/turf';
import {
  US_STATE_ABBR_TO_NAME,
  US_STATE_NAME_TO_ABBR,
  normalizeLocationText,
  canonicalizeStateToken,
  stateSynonymForms,
} from './searchUtils';

const STATE_ABBR_TO_FIPS = {
  al: '01', ak: '02', az: '04', ar: '05', ca: '06', co: '08', ct: '09', de: '10',
  dc: '11', fl: '12', ga: '13', hi: '15', id: '16', il: '17', in: '18', ia: '19',
  ks: '20', ky: '21', la: '22', me: '23', md: '24', ma: '25', mi: '26', mn: '27',
  ms: '28', mo: '29', mt: '30', ne: '31', nv: '32', nh: '33', nj: '34', nm: '35',
  ny: '36', nc: '37', nd: '38', oh: '39', ok: '40', or: '41', pa: '42', ri: '44',
  sc: '45', sd: '46', tn: '47', tx: '48', ut: '49', vt: '50', va: '51', wa: '53',
  wv: '54', wi: '55', wy: '56',
};

const PLACES_BASE =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/Generalized_ACS2024/Places_CouSub_ConCity_SubMCD/MapServer';
// Generalized ACS 2024: 10 = Incorporated Places, 11 = Census Designated Places
const PLACE_LAYERS = [10, 11];

const placeCache = new Map(); // `${city}|${state}` → GeoJSON Feature | null

const placeInflight = new Map();

function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function padStateFips(value) {
  if (value == null || value === '') return null;
  const n = String(value).replace(/\D/g, '');
  if (!n) return null;
  return n.padStart(2, '0');
}

/** Venue state string (TX / Texas / 48) → 2-digit Census FIPS. */
export function stateTokenToFips(token) {
  const key = normalizeLocationText(token);
  if (!key) return null;
  if (/^\d{1,2}$/.test(key)) return padStateFips(key);
  if (STATE_ABBR_TO_FIPS[key]) return STATE_ABBR_TO_FIPS[key];
  const abbr = US_STATE_NAME_TO_ABBR[key];
  if (abbr && STATE_ABBR_TO_FIPS[abbr]) return STATE_ABBR_TO_FIPS[abbr];
  return null;
}

export function normalizeCityName(value) {
  let name = normalizeLocationText(value);
  name = name
    .replace(/\b(st|saint)\.?\b/g, 'st')
    .replace(/\b(ft|fort)\.?\b/g, 'ft')
    .replace(/\s+(city|township|village|borough|town|cdp|municipality)$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return name;
}

/**
 * Canonical state key for resource index lookups (prefer USPS abbr: "tx").
 * Falls back to normalized raw text when the token is unknown.
 */
export function canonicalStateResourceKey(token) {
  const canon = canonicalizeStateToken(token);
  if (canon) return US_STATE_NAME_TO_ABBR[canon] || canon;
  return normalizeLocationText(token) || null;
}

export function canonicalCityStateResourceKey(city, state) {
  const cityKey = normalizeCityName(city);
  const stateKey = canonicalStateResourceKey(state);
  if (!cityKey || !stateKey) return null;
  return `${cityKey}|${stateKey}`;
}

/**
 * Index keys for a state token so "TX" and "Texas" land on the same lookups.
 * Includes raw normalized text, full name, and abbreviation when known.
 */
export function stateResourceIndexKeys(token) {
  const keys = new Set();
  const forms = stateSynonymForms(token);
  forms.forEach((form) => {
    if (form) keys.add(form);
  });
  const norm = normalizeLocationText(token);
  if (norm) keys.add(norm);
  const canon = canonicalizeStateToken(token);
  if (canon) {
    keys.add(canon);
    const abbr = US_STATE_NAME_TO_ABBR[canon];
    if (abbr) keys.add(abbr);
  }
  const primary = canonicalStateResourceKey(token);
  if (primary) keys.add(primary);
  return [...keys].filter(Boolean);
}

export function cityStateResourceIndexKeys(city, state) {
  const cityKey = normalizeCityName(city);
  if (!cityKey) return [];
  return stateResourceIndexKeys(state).map((stateKey) => `${cityKey}|${stateKey}`);
}

/**
 * Does this venue carry an approved resource category via venue / city / state scope?
 * Matches TX↔Texas and normalized city names the same way area polygons do.
 */
export function venueHasCategoryInResourceIndex(index, venue, categoryId) {
  if (!index || !venue || !categoryId) return false;
  const vid = venue.id != null ? String(venue.id) : '';
  if (vid && index.byVenueId?.get(vid)?.has(categoryId)) return true;

  const stateKey = canonicalStateResourceKey(venue.state);
  if (stateKey && index.byState?.get(stateKey)?.has(categoryId)) return true;

  const cityStateKey = canonicalCityStateResourceKey(venue.city, venue.state);
  if (cityStateKey && index.byCityState?.get(cityStateKey)?.has(categoryId)) return true;

  // Legacy index keys (pre-normalization) — still honor plain lowercase matches.
  for (const key of stateResourceIndexKeys(venue.state)) {
    if (index.byState?.get(key)?.has(categoryId)) return true;
  }
  for (const key of cityStateResourceIndexKeys(venue.city, venue.state)) {
    if (index.byCityState?.get(key)?.has(categoryId)) return true;
  }
  return false;
}

/**
 * Build the Tour Planner resource category index from approved submissions.
 * Area scopes are stored under a canonical TX/Texas key so pin badges match.
 */
export function buildVenueResourceIndex(submissions = []) {
  const byVenueId = new Map();
  const byState = new Map();
  const byCityState = new Map();

  const addToSetMap = (map, key, category) => {
    if (!key || !category) return;
    let set = map.get(key);
    if (!set) {
      set = new Set();
      map.set(key, set);
    }
    set.add(category);
  };

  (submissions || []).forEach((data) => {
    if (!data) return;
    const category = data.category;
    if (!category) return;
    if (data.venueId) addToSetMap(byVenueId, String(data.venueId), category);

    const scope = String(data.scope || 'venue').toLowerCase();
    if (scope === 'state') {
      const stateKey = canonicalStateResourceKey(data.venueState);
      if (stateKey) addToSetMap(byState, stateKey, category);
    } else if (scope === 'city') {
      const cityStateKey = canonicalCityStateResourceKey(data.venueCity, data.venueState);
      if (cityStateKey) addToSetMap(byCityState, cityStateKey, category);
    }
  });

  return { byVenueId, byState, byCityState };
}

function sqlLiteral(value) {
  return String(value || '').replace(/'/g, "''").toUpperCase();
}

function setCountMatches(set, categoryIds) {
  if (!set || !categoryIds?.length) return false;
  return categoryIds.every((id) => set.has(id));
}

/**
 * Which city/state areas carry every selected resource category.
 * Uses the same index TourPlanner already builds from resource_submissions.
 */
export function collectResourceCoverage(index, categoryIds) {
  const states = [];
  const cities = [];
  if (!index || !categoryIds?.length) return { states, cities };
  (index.byState || new Map()).forEach((set, stateKey) => {
    if (setCountMatches(set, categoryIds)) states.push(stateKey);
  });
  (index.byCityState || new Map()).forEach((set, cityStateKey) => {
    if (!setCountMatches(set, categoryIds)) return;
    const split = cityStateKey.indexOf('|');
    if (split < 0) return;
    cities.push({
      key: cityStateKey,
      city: cityStateKey.slice(0, split),
      state: cityStateKey.slice(split + 1),
    });
  });
  return { states, cities };
}

function venueInState(venue, stateKey) {
  return normalizeLocationText(venue?.state) === normalizeLocationText(stateKey)
    || stateTokenToFips(venue?.state) === stateTokenToFips(stateKey);
}

function venueInCity(venue, city, state) {
  return normalizeCityName(venue?.city) === normalizeCityName(city) && venueInState(venue, state);
}

export function countVenuesInState(venues, stateKey) {
  return (venues || []).filter((v) => venueInState(v, stateKey)).length;
}

export function countVenuesInCity(venues, city, state) {
  return (venues || []).filter((v) => venueInCity(v, city, state)).length;
}

/**
 * Fixed small-cluster orange (not density-based).
 * Fill uses affiliate-style alpha; outline is the solid hue.
 */
export function resourceAreaStyle(clusterOrange = '#e67e22') {
  const hex = clusterOrange || '#e67e22';
  return {
    color: hex,
    fillColor: hexToRgba(hex, 0.28),
    opacity: 0.28,
  };
}

function pickBestPlace(features, cityName) {
  const want = normalizeCityName(cityName);
  const scored = (features || [])
    .filter((f) => f?.geometry)
    .map((f) => {
      const base = normalizeCityName(f.properties?.BASENAME || f.properties?.NAME);
      let score = 0;
      if (base === want) score += 4;
      else if (base.startsWith(want) || want.startsWith(base)) score += 1;
      return { f, score, base };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored[0]?.f || null;
}

async function queryPlaceLayer(layerId, where) {
  const params = new URLSearchParams({
    where,
    outFields: 'GEOID,NAME,BASENAME,STATE',
    returnGeometry: 'true',
    outSR: '4326',
    maxAllowableOffset: '0.004',
    geometryPrecision: '5',
    f: 'geojson',
  });
  const res = await fetch(`${PLACES_BASE}/${layerId}/query?${params.toString()}`);
  if (!res.ok) throw new Error(`Place query failed (${res.status})`);
  return res.json();
}

async function fetchPlacesForStateCities(fips, cityNames) {
  const unique = [...new Set(cityNames.map(normalizeCityName).filter(Boolean))];
  if (!fips || unique.length === 0) return [];
  const inList = unique.map((c) => `'${sqlLiteral(c)}'`).join(',');
  const where = `STATE='${fips}' AND UPPER(BASENAME) IN (${inList})`;
  const collections = await Promise.all(
    PLACE_LAYERS.map((layerId) =>
      queryPlaceLayer(layerId, where).catch((err) => {
        console.warn(`Resource area map: place layer ${layerId} failed`, err);
        return { features: [] };
      })
    )
  );
  const byCity = new Map();
  collections.forEach((fc) => {
    (fc.features || []).forEach((f) => {
      const cityKey = normalizeCityName(f.properties?.BASENAME || f.properties?.NAME);
      if (!cityKey || byCity.has(cityKey)) return; // first layer (incorporated) wins
      f.properties = {
        ...(f.properties || {}),
        regionId: String(f.properties.GEOID || `${fips}-${cityKey}`),
      };
      byCity.set(cityKey, f);
    });
  });
  return unique.map((city) => {
    const exact = byCity.get(city);
    if (exact) return { city, feature: exact };
    const fallback = pickBestPlace([...byCity.values()], city);
    return { city, feature: fallback };
  });
}

/**
 * Load Census place polygons for city+state pairs. Cached for the session.
 * @param {Array<{ city: string, state: string, key?: string }>} cityEntries
 * @returns {Promise<Map<string, object|null>>} city|state → Feature
 */
export async function fetchCityPlacePolygons(cityEntries) {
  const result = new Map();
  const inflightWaits = [];
  const pendingByFips = new Map();

  for (const entry of cityEntries || []) {
    const city = normalizeCityName(entry.city);
    const state = normalizeLocationText(entry.state);
    const key = entry.key || `${(entry.city || '').trim().toLowerCase()}|${(entry.state || '').trim().toLowerCase()}`;
    if (placeCache.has(key)) {
      result.set(key, placeCache.get(key));
      continue;
    }
    if (placeInflight.has(key)) {
      inflightWaits.push(
        placeInflight.get(key).then((feat) => {
          result.set(key, feat);
        })
      );
      continue;
    }
    const fips = stateTokenToFips(entry.state);
    if (!fips || !city) {
      placeCache.set(key, null);
      result.set(key, null);
      continue;
    }
    if (!pendingByFips.has(fips)) pendingByFips.set(fips, []);
    pendingByFips.get(fips).push({ key, city, state });
  }

  const jobs = [...inflightWaits];
  pendingByFips.forEach((entries, fips) => {
    const promise = fetchPlacesForStateCities(fips, entries.map((e) => e.city))
      .then((rows) => {
        const byNormCity = new Map();
        rows.forEach((row) => {
          if (row?.city) byNormCity.set(row.city, row.feature || null);
        });
        entries.forEach((e) => {
          const feat = byNormCity.get(normalizeCityName(e.city)) || null;
          placeCache.set(e.key, feat);
          placeInflight.delete(e.key);
          result.set(e.key, feat);
        });
      })
      .catch((err) => {
        console.warn('Resource area map: city polygon fetch failed', err);
        entries.forEach((e) => {
          placeCache.set(e.key, null);
          placeInflight.delete(e.key);
          result.set(e.key, null);
        });
      });
    entries.forEach((e) => {
      placeInflight.set(e.key, promise.then(() => placeCache.get(e.key)));
    });
    jobs.push(promise);
  });

  await Promise.all(jobs);
  return result;
}

/** Convex hull / buffer fallback when Census has no matching place. */
export function hullForCityVenues(venues, city, state) {
  const pts = (venues || [])
    .filter((v) => venueInCity(v, city, state))
    .map((v) => {
      const lat = parseFloat(v.latitude);
      const lng = parseFloat(v.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return turf.point([lng, lat]);
    })
    .filter(Boolean);
  if (pts.length === 0) return null;
  try {
    if (pts.length === 1) {
      return turf.buffer(pts[0], 6, { units: 'kilometers' });
    }
    if (pts.length === 2) {
      return turf.buffer(turf.lineString(pts.map((p) => p.geometry.coordinates)), 4, { units: 'kilometers' });
    }
    const fc = turf.featureCollection(pts);
    const hull = turf.convex(fc);
    if (!hull) return turf.buffer(pts[0], 6, { units: 'kilometers' });
    return turf.buffer(hull, 1.5, { units: 'kilometers' });
  } catch (_) {
    return null;
  }
}

export function findStateFeature(statesFc, stateKey) {
  if (!statesFc?.features?.length) return null;
  const fips = stateTokenToFips(stateKey);
  const name = US_STATE_ABBR_TO_NAME[normalizeLocationText(stateKey)]
    || normalizeLocationText(stateKey);
  return statesFc.features.find((f) => {
    const id = padStateFips(f.properties?.regionId || f.id);
    const fname = normalizeLocationText(f.properties?.name);
    if (fips && id === fips) return true;
    if (name && fname === name) return true;
    return false;
  }) || null;
}

export { US_STATE_ABBR_TO_NAME };
